import { useState, type CSSProperties, type ReactElement, type ReactNode } from "react";

/**
 * Real photo with an automatic SVG fallback. Never probes whether the file
 * exists ahead of time — it just renders the <img> and swaps to `fallback`
 * on a real load failure (onError), so a missing/broken asset degrades to
 * the existing illustration instead of a broken-image icon.
 */
export function ProductImage(props: {
  src: string;
  alt: string;
  width: number;
  height: number;
  fallback: ReactNode;
  loading?: "eager" | "lazy";
  fetchPriority?: "high" | "low" | "auto";
  className?: string;
  /**
   * When true, the image fills whatever box the parent already establishes
   * (e.g. an absolutely-positioned banner layer, or a grid row stretched to
   * match its siblings) instead of reserving its own aspect-ratio box. Use
   * this for slots whose size is dictated by surrounding layout, not by the
   * image's own intrinsic ratio.
   */
  fill?: boolean;
  /** CSS object-fit for the real <img> (default "cover"). */
  objectFit?: "cover" | "contain";
  /** CSS object-position for the real <img> (default "center"). */
  objectPosition?: string;
}): ReactElement {
  const {
    src, alt, width, height, fallback, loading = "lazy", fetchPriority = "auto", className, fill = false,
    objectFit = "cover", objectPosition = "center",
  } = props;
  const [failed, setFailed] = useState(false);

  const wrapperStyle: CSSProperties = fill
    ? { display: "flex", alignItems: "center", justifyContent: "center", inlineSize: "100%", blockSize: "100%" }
    : { display: "block", inlineSize: "100%", aspectRatio: `${width} / ${height}` };

  if (failed) {
    return (
      <span
        className={className}
        style={wrapperStyle}
        role={alt ? "img" : undefined}
        aria-label={alt || undefined}
        aria-hidden={alt ? undefined : true}
      >
        {fallback}
      </span>
    );
  }

  return (
    <span className={className} style={wrapperStyle}>
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        onError={() => setFailed(true)}
        style={{ display: "block", inlineSize: "100%", blockSize: "100%", objectFit, objectPosition }}
      />
    </span>
  );
}
